"use client";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";

export default function PrivatePilotLicense() {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "introductie",
        "toelatingsvoorwaarden",
        "theorieopleiding",
        "praktijkopleiding",
        "type-rating",
        "elp",
        "examen",
        "licentie-geldigheid",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    { id: "introductie", label: "Introductie" },
    { id: "toelatingsvoorwaarden", label: "Toelatingsvoorwaarden" },
    { id: "theorieopleiding", label: "Theorieopleiding" },
    { id: "praktijkopleiding", label: "Praktijkopleiding" },
    { id: "type-rating", label: "Type Rating" },
    { id: "elp", label: "ELP Test" },
    { id: "examen", label: "Examen" },
    { id: "licentie-geldigheid", label: "Licentie & Geldigheid" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <div className="relative pt-32 sm:pt-36 pb-16 sm:pb-20 overflow-hidden">
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/videos/background.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Private Pilot Licence (Helicopter)
          </h1>
          <p className="text-lg sm:text-xl text-white/90">
            Alles wat je moet weten om privaat helikopterpiloot te worden
          </p>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="lg:sticky lg:top-24 bg-white p-6 rounded-xl shadow-lg border-t-4 border-accent">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Inhoud
              </h3>
              <ul className="space-y-2">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className={`block py-2 px-3 rounded-lg transition-colors ${
                        activeSection === section.id
                          ? "bg-accent text-white font-medium"
                          : "text-gray-700 hover:bg-gray-100 hover:text-accent"
                      }`}
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
              
              {/* Contact Button */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link
                  href="/#contact"
                  className="block w-full text-center py-2 px-4 border-2 border-accent text-accent font-medium rounded-full transition-all duration-300 hover:bg-accent hover:!text-white"
                >
                  Contact us
                </Link>
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 max-w-4xl">
            {/* Introduction */}
            <section id="introductie" className="mb-12 scroll-mt-24">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Wat moet je doen om privaat piloot Helicopter te worden en wat houdt
            dit allemaal in?
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">
              De <strong>PPL(H) – Private Pilot Licence</strong> en de{" "}
              <strong>LAPL(H) – Light Aircraft Pilot Licence</strong> licenties
              zijn de basislicenties die toelaten privé te vliegen met een
              helikopter, zonder commerciële doeleinden.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Je mag dus passagiers vervoeren, maar geen vergoeding vragen
              (behalve kosten delen onder bepaalde voorwaarden).
            </p>
            <p className="text-gray-700 leading-relaxed">
              De licentie is geldig in alle EASA-landen (Europa) en kan worden
              omgezet of uitgebreid naar een{" "}
              <strong>CPL(H) – Commercial Pilot Licence</strong> of{" "}
              <strong>ATPL(H) – Airline Transport Pilot Licence</strong> indien
              gewenst.
            </p>
          </div>

          <div className="mt-8 rounded-xl overflow-hidden shadow-lg border-t-4 border-accent">
            <Image
              src="/images/private_pilot.jpeg"
              alt="TopCat Helicopters Private Pilot — your passion, our experience, your future"
              width={1536}
              height={1024}
              className="w-full h-auto"
            />
          </div>
        </section>

        {/* Toelatingsvoorwaarden */}
        <section
          id="toelatingsvoorwaarden"
          className="mb-12 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-accent scroll-mt-24"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            2. Toelatingsvoorwaarden
          </h2>
          <div className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              <strong>Leeftijd:</strong> minimum 17 jaar voor de afgifte van de
              licentie (opleiding mag vroeger starten).
            </p>
            <div>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Medische geschiktheid:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>
                  EASA medisch Class 2 certificaat of LAPL certificaat,
                  afgeleverd door een AME (Aero-Medical Examiner).
                </li>
              </ul>
            </div>

            {/* Class 2 Medical */}
            <div className="bg-gray-50 p-6 rounded-lg mt-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Class 2-medisch certificaat
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Met een Class 2-medical mag men vliegen met eender welke
                helicopter binnen de categorie van de PPL – dus niet alleen
                lichte helikopters, maar ook grotere types en tweemotorige
                toestellen – zolang het niet om commerciële vluchten gaat.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Het certificaat is bovendien de noodzakelijke medische basis
                voor wie later een commerciële licentie (CPL) of instrument
                rating (IR) wil behalen.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Initieel medisch onderzoek:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Uitgebreide klinische evaluatie</li>
                <li>Oogonderzoek met onderzoek van contrast zicht</li>
                <li>ECG</li>
                <li>Urine onderzoek</li>
                <li>Evaluatie van mentale toestand</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bijkomende onderzoeken kunnen vereist zijn bij specifieke
                medische indicaties of op hogere leeftijd.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Op regelmatige basis moet boven de 40 jaar een cardiovasculaire
                risico analyse gebeuren waarvoor een bloedonderzoek noodzakelijk
                is met bepaling van cholesterol en een evaluatie van mogelijke
                risico&apos;s op obstructieve slaapapneu.
              </p>
              <p className="text-gray-700 leading-relaxed mb-2">
                <strong>Geldigheid:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Tot 40 jaar: vijf jaar geldig</li>
                <li>Tussen 40 en 50 jaar: twee jaar geldig</li>
                <li>Vanaf vijftig jaar: één jaar geldig</li>
              </ul>
            </div>

            {/* LAPL Medical */}
            <div className="bg-gray-50 p-6 rounded-lg mt-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                LAPL-medisch certificaat
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Met een LAPL-medisch certificaat (Light Aircraft Pilot Licence)
                mag men enkel vliegen met een helikopter met een maximaal
                gewicht (MTOW = maximal take off weight) van 2000 kg en met
                maximaal 4 personen aan boord, zijnde dus de piloot en 3
                passagiers.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                De criteria voor het bekomen van het certificaat zijn minder
                streng.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Initieel medisch onderzoek:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Uitgebreide klinische evaluatie</li>
                <li>ECG</li>
                <li>Urine onderzoek</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bijkomende onderzoeken kunnen vereist zijn bij specifieke
                medische indicaties of op hogere leeftijd.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Op regelmatige basis moet boven de 40 jaar een cardiovasculaire
                risico analyse gebeuren waarvoor een bloedonderzoek noodzakelijk
                is met bepaling van cholesterol en een evaluatie van mogelijke
                risico&apos;s op obstructieve slaapapneu.
              </p>
              <p className="text-gray-700 leading-relaxed mb-2">
                <strong>Geldigheid:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Tot 40 jaar: vijf jaar geldig</li>
                <li>Vanaf 40: twee jaar geldig</li>
              </ul>
            </div>

            <div className="bg-accent/10 border-l-4 border-accent p-4 mt-6">
              <p className="text-gray-800 leading-relaxed">
                <strong>In de praktijk:</strong> het LAPL-medical is ideaal voor
                wie louter recreatief wil vliegen met lichte toestellen en geen
                ambitie heeft om verder te gaan in de vliegopleiding. Het Class
                2-medical is de meer toekomstgerichte keuze — het is iets
                strenger, maar opent alle mogelijkheden voor verdere opleidingen,
                ratings en types.
              </p>
            </div>
          </div>
        </section>

        {/* Theorieopleiding */}
        <section
          id="theorieopleiding"
          className="mb-12 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-accent scroll-mt-24"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            3. Theorieopleiding
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            De theoretische cursus omvat 9 vakken, meestal gegeven in modules
            door een erkende vliegschool (ATO – Approved Training Organisation):
          </p>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>Luchtvaartregelgeving</li>
            <li>Human performance en beperkingen</li>
            <li>Meteorologie</li>
            <li>Communicatie (radio-telefonie)</li>
            <li>Navigatie</li>
            <li>Vluchtplanning en prestatieberekening</li>
            <li>Principes van de vlucht (aerodynamica)</li>
            <li>Operationele procedures</li>
            <li>Algemene kennis van het luchtvaartuig (helikopterspecifiek)</li>
          </ol>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Minimaal vereiste opleiding:
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>Reken op ± 100 tot 120 uur studie tijd</li>
            <li>
              De theorie-examens worden afgenomen door de Belgische
              burgerluchtvaart (DGTA), via meerkeuzevragen. Hier dient u alle
              examens op een dag af te leggen.
            </li>
            <li>
              U kan de examens ook afleggen in een ander Europees land
              bijvoorbeeld in Nederland waar u examen per examen kan afleggen.
            </li>
            <li>
              De geldigheid van het theoriecertificaat is 24 maand na het
              afleggen van het laatste theorie examen. Dit wil zeggen dat u ten
              laatste binnen de 2 jaar na het afleggen van uw theorie examens uw
              praktisch examen moet af te leggen ander vervalt uw theorie en
              moet u alle examens opnieuw doen.
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Bij TopCat Helicopter kan u de theorie op verschillende manieren
            volgen:
          </h3>
          <ul className="list-disc list-inside space-y-3 text-gray-700 ml-4">
            <li>
              Klassikaal in de West Aviation Club te Koksijde of de Limburgse
              vleugels te Zwartberg
            </li>
            <li>
              Door zelfstudie onder begeleiding via:
              <ul className="list-circle list-inside ml-8 mt-2 space-y-2">
                <li>
                  <a
                    href="https://benedictaviation.be/pplh/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    Benedict Aviation
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.blubird.nl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline"
                  >
                    Blue Bird in Nederland
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </section>

        {/* Praktijkopleiding */}
        <section
          id="praktijkopleiding"
          className="mb-12 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-accent scroll-mt-24"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            4. Praktijkopleiding
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            De vliegopleiding moet gebeuren in een erkende
            helikoptervliegschool.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>TopCat Helicopters is een erkende school BE.DTO.134.</strong>
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Vereisten:
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>Minimaal aantal vlieguren: 45 uur</strong>, waarvan:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>Minstens 25 uur dual (met instructeur)</li>
            <li>
              Minstens 10 uur solo, waarvan:
              <ul className="list-circle list-inside ml-8 mt-2 space-y-1">
                <li>5 uur solo overlandvlucht</li>
                <li>
                  1 overlandvlucht van minstens 100 NM (185 km) met landingen op
                  twee andere luchthavens of heliports dan het vertrekpunt
                </li>
              </ul>
            </li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>Daarnaast omvat de opleiding ook:</strong>
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>Preflight procedures, technische kennis, checks</li>
            <li>
              Basismanoeuvres (hovering, take-off, landing, autorotatie,
              navigatie, noodgevallen)
            </li>
            <li>Communicatie en radioprocedures</li>
            <li>Cross-country navigatie</li>
            <li>Luchtruimgebruik en situational awareness</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-4">
            De opleiding mag niet minder dan 20 uur bedragen vóór de eerste
            solo.
          </p>
          <p className="text-gray-700 leading-relaxed">
            De meeste kandidaten behalen hun licentie na 50–60 uur totaal,
            afhankelijk van frequentie, aanleg en weersomstandigheden.
          </p>
        </section>

        {/* Type Rating */}
        <section
          id="type-rating"
          className="mb-12 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-accent scroll-mt-24"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            5. Type rating
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            De PPL(H) wordt altijd afgegeven met een typebevoegdheid voor het
            toestel waarop de opleiding is gevolgd.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Bij TopCat worden de opleidingen gebruikelijk gegeven op volgende
            toestellen:
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>R22 (Robinson R22)</li>
            <li>R44 (Robinson R44)</li>
            <li>HU269 (Hughes 269)</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            Wil je later op een ander type vliegen, dan moet je een bijkomende
            type rating volgen voor dat specifieke toestel gebruikelijk minimaal
            5 training + check.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Opleidingen op volgende toestellen zijn ook mogelijk in specifieke
            gevallen:
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li>Bell 47</li>
            <li>Bell 206</li>
            <li>Bell 505</li>
            <li>EC120</li>
            <li>MD500</li>
            <li>Gazelle 341/342</li>
          </ul>
        </section>

        {/* ELP */}
        <section
          id="elp"
          className="mb-12 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-accent scroll-mt-24"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            6. ELP (English Language Proficiency test)
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            De ELP is het bewijs dat je Engels voldoende beheerst om veilig te
            communiceren in de lucht. Zonder ELP blijf je letterlijk &quot;aan
            de grond&quot; zodra je in Engels luchtruim of een internationale
            omgeving terechtkomt.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-8">
            Hoe de test verloopt
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            De test bestaat uit twee hoofdonderdelen:
          </p>

          <div className="space-y-4 mb-6">
            <div>
              <p className="text-gray-800 font-semibold mb-2">
                Luister- en begripstest:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>
                  Je hoort luchtvaartcommunicatie (ATC–piloot) in verschillende
                  situaties
                </li>
                <li>
                  Je moet tonen dat je begrijpt wat er gebeurt en gepast kunt
                  reageren
                </li>
              </ul>
            </div>

            <div>
              <p className="text-gray-800 font-semibold mb-2">
                Gesprek of interview:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>
                  Een examinator (meestal een piloot of linguïst) voert een
                  gesprek over algemene luchtvaartonderwerpen
                </li>
                <li>
                  Soms met foto&apos;s of scenario&apos;s (bijv. &quot;je ziet
                  een brand op de startbaan, wat zeg je aan de toren?&quot;)
                </li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Er wordt geëvalueerd op zes criteria:
          </h3>
          <ul className="list-disc list-inside space-y-1 text-gray-700 mb-6 ml-4">
            <li>Uitspraak</li>
            <li>Structuur</li>
            <li>Woordenschat</li>
            <li>Vloeiendheid</li>
            <li>Begrip</li>
            <li>Interactie</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Waar en wanneer
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>
              De ELP-test wordt afgenomen door een erkend ELP-testcentrum
            </li>
            <li>De test duurt ongeveer 20 tot 30 minuten</li>
            <li>
              Je doet best de test voor het afleggen van je praktisch examen
            </li>
            <li>
              Resultaat: je krijgt een ICAO English Level 4, 5 of 6 dat op je
              licentie wordt vermeld
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Waarom het belangrijk is
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>
              Zelfs als je enkel in België vliegt, is Engels vaak de
              standaardtaal op grotere luchthavens of bij transitie van
              luchtruim
            </li>
            <li>
              Een geldige ELP voorkomt communicatieproblemen en is verplicht
              voor het gebruik van de Engelse radiotaal
            </li>
            <li>
              Zonder geldige ELP mag je niet vliegen in gecontroleerde zones
            </li>
          </ul>

          <div className="bg-accent/10 border-l-4 border-accent p-4">
            <p className="text-gray-800 font-semibold">Kort gezegd:</p>
            <p className="text-gray-700 leading-relaxed mt-2">
              De ELP is het bewijs dat je Engels voldoende beheerst om veilig te
              communiceren in de lucht. Zonder ELP blijf je letterlijk
              &quot;aan de grond&quot; zodra je in een gecontroleerde zone komt.
            </p>
          </div>
        </section>

        {/* Examen */}
        <section
          id="examen"
          className="mb-12 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-accent scroll-mt-24"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            7. Examen (Skill Test)
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Na voltooiing van de opleiding:
          </p>
          <ul className="list-none space-y-2 text-gray-700 mb-6 ml-4">
            <li>✅ Theorie geslaagd</li>
            <li>✅ Alle vliegvereisten voldaan</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-4">
            Dan volgt het praktisch examen (skill test) met een examinator
            (FE-H), erkend door de DGTA.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            De test omvat:
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>Preflight planning en briefing</li>
            <li>Check procedures</li>
            <li>
              Vluchtmanoeuvres (hover, circuits, navigatie, autorotaties,
              noodprocedures, precisielandingen, enz.)
            </li>
            <li>Radiocommunicatie en luchtvaartnavigatie</li>
          </ul>

          <p className="text-gray-700 leading-relaxed">
            Na slagen wordt de licentie aangevraagd bij de autoriteit waar u de
            licentie wenst wat in gans Europa kan zijn.
          </p>
        </section>

        {/* Licentie en geldigheid */}
        <section
          id="licentie-geldigheid"
          className="mb-12 bg-white p-6 sm:p-8 rounded-xl shadow-lg border-t-4 border-accent scroll-mt-24"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            8. Licentie en geldigheid
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">De PPL(H) is:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6 ml-4">
            <li>
              Onbeperkt geldig, maar de type rating van de toestellen waarmee u
              vliegt moeten jaarlijks worden vernieuwd
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Voor de jaarlijkse verlenging is vereist:
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
            <li>
              1 uur recente vliegervaring op type in de laatste 12 maanden
            </li>
            <li>In het bezit zijn van een geldig medisch certificaat</li>
            <li>
              In het bezit zijn van een geldige ELP (English Language
              Proficiency document)
            </li>
          </ul>
        </section>

        {/* CTA */}
        <section className="text-center bg-gradient-to-r from-accent/10 to-accent/5 p-8 rounded-xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Klaar om te beginnen?
          </h2>
          <p className="text-gray-700 mb-6">
            Neem contact met ons op voor meer informatie over de PPL(H)
            opleiding
          </p>
          <Link
            href="/#contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-accent text-white font-medium rounded-full hover:bg-accent/90 transition-all duration-300 transform hover:scale-105"
          >
            Contacteer ons
          </Link>
        </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
